import {
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import React, { useMemo, useState } from 'react';
import { HIGHLIGHT_CLASS_EXTRA_OCCURRENCE } from 'shared/components/ui/datepicker';
import { PopupMenu, PopupMenuList } from 'shared/components/ui/popup-menu';
import { weekdaysNumberMap } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { getTaskPlanning } from 'shared/utils/get-task-planning';

import { useData } from '../../use-data';
import * as Styled from './extra-occurrence-datepicker.style';

export type ExtraOccurrenceDatepickerProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children'
> & {
  task: Task;
  value: Timestamp | undefined;
  onAdd: (date: Timestamp) => void;
  onRemove: (date: Timestamp) => void;
  initialMonth?: Date;
};

export const ExtraOccurrenceDatepicker: React.FC<
  ExtraOccurrenceDatepickerProps
> = ({ task, value, onAdd, onRemove, initialMonth, ...rest }) => {
  const today = useToday();
  const { weekStartsOn, dateFormat } = useData();

  // Track the currently displayed month to optimize planning calculations
  const [displayedMonth, setDisplayedMonth] = useState(
    initialMonth || value || today,
  );

  // Calculate planning only for the relevant range (displayed month's visible weeks)
  const planningRange = useMemo(
    () => ({
      start: startOfWeek(startOfMonth(displayedMonth), {
        weekStartsOn: weekdaysNumberMap[weekStartsOn],
      }),
      end: endOfWeek(endOfMonth(displayedMonth), {
        weekStartsOn: weekdaysNumberMap[weekStartsOn],
      }),
    }),
    [displayedMonth, weekStartsOn],
  );

  const plannedDates = useMemo(() => {
    const planning = getTaskPlanning(task, {
      startDate: planningRange.start,
      endDate: planningRange.end,
      today,
      weekStartsOn,
    });
    return new Set(planning.map((p) => p.date.getTime()));
  }, [task, planningRange, today, weekStartsOn]);

  const handleDateChange = (date: Timestamp | null) => {
    if (!date) {
      return;
    }

    if (plannedDates.has(date.getTime())) {
      onRemove(date);
      return;
    }

    onAdd(date);
  };

  const highlightDates = useMemo(() => {
    return [
      {
        [HIGHLIGHT_CLASS_EXTRA_OCCURRENCE]: Array.from(plannedDates).map((d) =>
          startOfDay(new Date(d)),
        ),
      },
    ];
  }, [plannedDates]);

  return (
    <PopupMenu {...rest}>
      <PopupMenuList>
        <Styled.StyledDatepicker
          today={today}
          selected={value}
          openToDate={initialMonth}
          onChange={handleDateChange}
          dateFormat={dateFormat}
          weekStartsOn={weekStartsOn}
          highlightDates={highlightDates}
          onMonthChange={setDisplayedMonth}
          shouldCloseOnSelect={false}
          inline
        />
      </PopupMenuList>
    </PopupMenu>
  );
};
